import { fmtDate } from '@dcc-erp/shared';
import type { ApprovalEvent, ApprovalEventType } from '@dcc-erp/shared';

// ============================================================
// Shared audit-log panel (Component 7) — the immutable, chronological event
// stream for a document. Distinct from ApprovalTimeline (which shows the current
// mutable chain state): this retains every prior event, including the
// return→resubmit cycles whose live approval rows are regenerated.
//
// Presentational only — the parent wires the data (useJournalApprovalEvents /
// usePaymentRequestApprovalEvents).
// ============================================================

const EVENT_LABEL: Record<ApprovalEventType, string> = {
  CREATED: 'สร้างเอกสาร',
  SUBMITTED: 'ส่งอนุมัติ',
  RESUBMITTED: 'ส่งอนุมัติอีกครั้ง',
  REVIEWED: 'ตรวจสอบ',
  APPROVED: 'อนุมัติ',
  REJECTED: 'ไม่อนุมัติ',
  RETURNED: 'ตีกลับแก้ไข',
  EDITED: 'แก้ไข',
  POSTED: 'ผ่านรายการ',
  PAID: 'จ่ายชำระ',
  CANCELLED: 'ยกเลิก',
  DELETED: 'ลบ',
  REVERSED: 'กลับรายการ',
};

const TONE: Partial<Record<ApprovalEventType, string>> = {
  APPROVED: 'var(--positive)',
  POSTED: 'var(--positive)',
  PAID: 'var(--positive)',
  REJECTED: 'var(--negative)',
  DELETED: 'var(--negative)',
  CANCELLED: 'var(--negative)',
  REVERSED: 'var(--negative)',
  RETURNED: 'var(--warning)',
};

function fmtTime(d: string | Date): string {
  const dt = d instanceof Date ? d : new Date(d);
  return `${fmtDate(dt)} ${dt.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}`;
}

// Render an unknown JSON scalar as text. Only primitives are surfaced — objects
// are ignored (the detail snapshot only stores scalars + the role-code chain).
function scalar(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
}

// Pull a few human-meaningful fields out of the JSON detail snapshot.
function summarizeDetail(detail: ApprovalEvent['detail']): string | null {
  if (!detail || typeof detail !== 'object') return null;
  const d: Record<string, unknown> = detail;
  const bits: string[] = [];
  const amount = scalar(d.amount);
  if (amount != null) bits.push(`ยอด ${amount}`);
  if (Array.isArray(d.chain) && d.chain.length > 0) {
    bits.push(`สาย: ${d.chain.map((c) => scalar(c) ?? '').join(' → ')}`);
  }
  const period = scalar(d.period);
  if (period != null) bits.push(`งวด ${period}`);
  const lineCount = scalar(d.lineCount);
  if (lineCount != null) bits.push(`${lineCount} บรรทัด`);
  const cashAccount = scalar(d.cashAccountCode);
  if (cashAccount != null) bits.push(`บัญชีจ่าย ${cashAccount}`);
  const category = scalar(d.categoryCode);
  if (category != null) bits.push(`หมวด ${category}`);
  return bits.length > 0 ? bits.join(' · ') : null;
}

export function AuditLog({ events }: { events: ApprovalEvent[] }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
        ประวัติการดำเนินการ
      </div>
      {events.length === 0 ? (
        <div className="meta">ยังไม่มีประวัติ</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {events.map((e) => {
            const detailText = summarizeDetail(e.detail);
            return (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: '#fafafa',
                }}
              >
                <div style={{ minWidth: 120, fontSize: 12, color: 'var(--text-muted)' }}>
                  {fmtTime(e.createdAt)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>
                    <span style={{ fontWeight: 600, color: TONE[e.eventType] ?? 'var(--text)' }}>
                      {EVENT_LABEL[e.eventType] ?? e.eventType}
                    </span>
                    {e.stepNo != null && (
                      <span className="meta" style={{ marginLeft: 6 }}>
                        ขั้น {e.stepNo}
                      </span>
                    )}
                  </div>
                  <div className="meta">
                    {e.actorName}
                    {e.roleLabel ? ` · ${e.roleLabel}` : ''}
                  </div>
                  {e.reason && (
                    <div className="meta" style={{ fontStyle: 'italic' }}>
                      &ldquo;{e.reason}&rdquo;
                    </div>
                  )}
                  {detailText && (
                    <div className="meta" style={{ fontSize: 12 }}>
                      {detailText}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
